import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getSupportTickets, getSupportTicketStats, createSupportTicket,
  assignSupportTicket, updateSupportTicketStatus, resolveSupportTicket,
  addSupportTicketRemark, getAgencies, getStaff, uploadFile, getMaxBookingQty, validateTxn,
  escalateSupportTicket, addLpgEntries, addLpgEntriesExcel, updateLpgEntry,
} from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Headset, Plus, Tag, Clock, CheckCircle, XCircle, Spinner,
  UploadSimple, CalendarBlank, Warning, ArrowRight, ChatText, Scales, User,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import CardComplaintPanel from '../components/support/CardComplaintPanel';

const QUERY_TYPES = [
  'Booking Mismatch', 'Amount Mismatch', 'Payment Issue',
  'Report Issue', 'Bill Payment Issue', 'General Support',
];

// Role-specific query type overrides. Keys are user.role values.
const ROLE_QUERY_TYPES = {
  card_vendor: [
    'Card Defective', 'Card Not Received', 'Limit Mismatch',
    'WB / Payment Not Received', 'Duplicate Card', 'General Support',
  ],
  gift_card_vendor: [
    'Voucher Invalid', 'Voucher Already Used', 'Payment Not Received',
    'Complaint Dispute', 'Voucher Amount Mismatch', 'General Support',
  ],
  recharge_vendor: [
    'Recharge Failed', 'Amount Mismatch', 'Payment Not Received',
    'Wrong Number Recharged', 'General Support',
  ],
  electricity_vendor: [
    'Bill Payment Failed', 'Amount Mismatch', 'Payment Not Received',
    'Wrong Consumer Number', 'General Support',
  ],
  sales_person: [
    'Commission Mismatch', 'Missing Commission', 'Report Issue', 'General Support',
  ],
  sub_staff: [
    'Booking Mismatch', 'Amount Mismatch', 'Report Issue', 'General Support',
  ],
};

function getQueryTypesForRole(role) {
  return ROLE_QUERY_TYPES[role] || QUERY_TYPES;
}

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'rejected'];

const ESCALATION_INFO = {
  1: { label: 'Level 1 — Customer Support', name: 'Customer Support', email: 'support@axialup.in', time: '4-5 Working Days', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  2: { label: 'Level 2 — Operations Manager', name: 'Ghanshyam', email: 'ghanshyam@axialup.in', time: '2-3 Working Days', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  3: { label: 'Level 3 — Management', name: 'Raviraj', email: 'raviraj@axialup.in', time: '2-3 Working Days', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, rejected: 0, total: 0 });
  const [agencies, setAgencies] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAgency, setFilterAgency] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Create form
  const todayDD = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' }).replace(/\//g, '-');
  const [form, setForm] = useState({
    query_type: '', agency_id: '', booking_date: todayDD,
    transaction_id: '', booked_qty: '', confirmed_qty: '',
    amount: '', description: '', file_urls: [],
  });

  // Detail actions
  const [assignTo, setAssignTo] = useState('');
  const [remarkText, setRemarkText] = useState('');
  const [resolveNotes, setResolveNotes] = useState('');
  const [adjQty, setAdjQty] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [newLpgId, setNewLpgId] = useState('');

  // LPG Entries management
  const [lpgPasteText, setLpgPasteText] = useState('');
  const [lpgUpdating, setLpgUpdating] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';
  const isDistributor = user?.role === 'distributor';
  const isVendor = ['gift_card_vendor', 'recharge_vendor', 'electricity_vendor', 'card_vendor'].includes(user?.role);

  const agencyFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Agencies' },
    ...agencies.map(a => ({ value: a.id, label: a.name }))
  ], [agencies]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.query_type = filterType;
      if (filterAgency !== 'all') params.agency_id = filterAgency;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      // Vendors can't access /api/agencies (403) — skip that call for them
      const promises = [
        getSupportTickets(params),
        getSupportTicketStats(),
      ];
      if (!isVendor) {
        promises.push(getAgencies());
      }
      const results = await Promise.all(promises);
      const [ticketsRes, statsRes, agRes] = results;
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      // Distributors only see their linked agencies
      const allAgencies = agRes ? agRes.data : [];
      let filteredAgencies;
      if (isDistributor && user?.agency_ids?.length) {
        filteredAgencies = allAgencies.filter(a => user.agency_ids.includes(a.id));
      } else {
        filteredAgencies = allAgencies;
      }
      setAgencies(filteredAgencies);
      // Auto-select agency if distributor has only one
      if (isDistributor && filteredAgencies.length === 1) {
        setForm(p => ({ ...p, agency_id: filteredAgencies[0].id }));
      }
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data);
      }
    } catch (err) {
      toast.error('Data load fail');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterAgency, filterStartDate, filterEndDate, isAdmin, isDistributor, isVendor, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const difference = (form.booked_qty && form.confirmed_qty)
    ? parseInt(form.booked_qty) - parseInt(form.confirmed_qty) : null;

  // Confirmed qty cannot be more than booked qty
  const confirmedExceedsBooked = difference !== null && difference < 0;

  // Max booking qty + duplicate check
  const [maxQtyInfo, setMaxQtyInfo] = useState({ max_qty: 0, already_adjusted: false, adjusted_ticket_id: null });
  useEffect(() => {
    if (form.agency_id && form.booking_date) {
      getMaxBookingQty(form.agency_id, form.booking_date, form.transaction_id || '').then(res => setMaxQtyInfo(res.data)).catch(() => {});
    } else {
      setMaxQtyInfo({ max_qty: 0, already_adjusted: false, adjusted_ticket_id: null });
    }
  }, [form.agency_id, form.booking_date, form.transaction_id]);

  const bookedQtyExceeded = maxQtyInfo.max_qty > 0 && form.booked_qty && parseInt(form.booked_qty) > maxQtyInfo.max_qty;
  const alreadyAdjusted = maxQtyInfo.already_adjusted;

  // TXN validation
  const [txnValid, setTxnValid] = useState({ valid: true, message: '', checked: false });
  useEffect(() => {
    if (form.transaction_id && form.agency_id && form.transaction_id.length >= 5) {
      const timer = setTimeout(() => {
        validateTxn(form.transaction_id, form.agency_id).then(res => setTxnValid({ ...res.data, checked: true })).catch(() => {});
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setTxnValid({ valid: true, message: '', checked: false });
    }
  }, [form.transaction_id, form.agency_id]);

  const txnInvalid = txnValid.checked && !txnValid.valid;

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await uploadFile(fd);
        uploaded.push(res.data.url);
      } catch (err) {
        toast.error(`Upload fail: ${file.name}`);
      }
    }
    setForm(p => ({ ...p, file_urls: [...p.file_urls, ...uploaded] }));
    if (uploaded.length) toast.success(`${uploaded.length} file(s) uploaded`);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!form.query_type) { toast.error('Query Type select karein'); return; }
    if (!form.description.trim()) { toast.error('Description zaroori hai'); return; }

    setSubmitting(true);
    try {
      const payload = {
        query_type: form.query_type,
        agency_id: form.agency_id || null,
        booking_date: form.booking_date,
        transaction_id: form.transaction_id,
        booked_qty: form.booked_qty ? parseInt(form.booked_qty) : null,
        confirmed_qty: form.confirmed_qty ? parseInt(form.confirmed_qty) : null,
        amount: form.amount ? parseFloat(form.amount) : null,
        description: form.description,
        file_urls: form.file_urls,
      };
      await createSupportTicket(payload);
      toast.success('Support ticket created!');
      setCreateOpen(false);
      setForm({ query_type: '', agency_id: '', booking_date: todayDD, transaction_id: '', booked_qty: '', confirmed_qty: '', amount: '', description: '', file_urls: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create fail');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = (ticket) => {
    setSelectedTicket(ticket);
    setAssignTo('');
    setRemarkText('');
    setResolveNotes('');
    setAdjQty('');
    setAdjAmount('');
    setResolveOpen(false);
    setDetailOpen(true);
  };

  const handleAssign = async () => {
    if (!assignTo) { toast.error('Select assignee'); return; }
    setActionLoading(true);
    try {
      await assignSupportTicket(selectedTicket.id, { assigned_to: assignTo });
      toast.success('Ticket assigned');
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign fail');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await updateSupportTicketStatus(selectedTicket.id, { status: newStatus, remarks: '' });
      toast.success(`Status updated: ${newStatus}`);
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status update fail');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemark = async () => {
    if (!remarkText.trim()) { toast.error('Remark likho'); return; }
    setActionLoading(true);
    try {
      await addSupportTicketRemark(selectedTicket.id, remarkText);
      toast.success('Remark added');
      setRemarkText('');
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remark fail');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolveNotes.trim()) { toast.error('Resolution notes zaroori hai'); return; }
    setActionLoading(true);
    try {
      const payload = {
        resolution_notes: resolveNotes,
        adjustment_qty: adjQty ? parseInt(adjQty) : null,
        adjustment_amount: adjAmount ? parseFloat(adjAmount) : null,
      };
      // If line items exist, send them (takes priority over simple qty/amount)
      if (lineItems.length > 0) {
        payload.line_items = lineItems.map(li => ({
          lpg_id: li.lpg_id,
          status: li.status,
          qty: li.qty,
          rate: li.rate || null,
          amount: null,
        }));
        payload.adjustment_qty = null;
        payload.adjustment_amount = null;
      }
      await resolveSupportTicket(selectedTicket.id, payload);
      toast.success('Ticket resolved!');
      setDetailOpen(false);
      setLineItems([]);
      setNewLpgId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolve fail');
    } finally {
      setActionLoading(false);
    }
  };

  const addLineItem = () => {
    if (!newLpgId.trim()) { toast.error('LPG ID enter karein'); return; }
    if (lineItems.some(li => li.lpg_id === newLpgId.trim())) { toast.error('Yeh LPG ID already add hai'); return; }
    // Find agency rate from selectedTicket
    const agency = agencies.find(a => a.id === selectedTicket?.agency_id);
    setLineItems(prev => [...prev, {
      lpg_id: newLpgId.trim(),
      status: 'success',
      qty: 1,
      rate: agency?.rate || 0,
    }]);
    setNewLpgId('');
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const removeLineItem = (idx) => {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const failedItems = lineItems.filter(li => li.status.toLowerCase() !== 'success');
  const totalRefundQty = failedItems.reduce((s, li) => s + (li.qty || 1), 0);
  const agency = agencies.find(a => a.id === selectedTicket?.agency_id);
  const agencyRate = agency?.rate || 0;
  const agencyFee = agency?.convenience_fee || 0;
  const totalRefundAmount = failedItems.reduce((s, li) => s + ((li.qty || 1) * ((li.rate || agencyRate) + agencyFee)), 0);

  // ─── LPG Entry Handlers ───
  const handleAddLpgBulk = async () => {
    if (!lpgPasteText.trim() || !selectedTicket) return;
    const ids = lpgPasteText.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.length >= 5);
    if (!ids.length) { toast.error('Koi valid LPG ID nahi mila'); return; }
    try {
      const res = await addLpgEntries(selectedTicket.id, ids);
      toast.success(`${res.data.added} LPG IDs added${res.data.skipped ? ` (${res.data.skipped} duplicate skipped)` : ''}`);
      setLpgPasteText('');
      // Refresh ticket
      const updated = await getSupportTickets({ ticket_id: selectedTicket.id });
      const found = (updated.data || []).find(t => t.id === selectedTicket.id);
      if (found) setSelectedTicket(found);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add fail'); }
  };

  const handleLpgExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTicket) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await addLpgEntriesExcel(selectedTicket.id, fd);
      toast.success(`${res.data.added} LPG IDs added from Excel`);
      e.target.value = '';
      const updated = await getSupportTickets();
      const found = (updated.data || []).find(t => t.id === selectedTicket.id);
      if (found) setSelectedTicket(found);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Excel upload fail'); }
  };

  const handleLpgStatusUpdate = async (lpgId, newStatus, remarks) => {
    if (!selectedTicket) return;
    setLpgUpdating(lpgId);
    try {
      const res = await updateLpgEntry(selectedTicket.id, lpgId, { status: newStatus, remarks: remarks || '' });
      if (res.data.refund_done) {
        toast.success(`${lpgId} → FAILED | Refund ₹${res.data.refund_amount} processed`);
      } else {
        toast.success(`${lpgId} → ${newStatus.toUpperCase()}`);
      }
      if (res.data.auto_closed) {
        toast.success('All entries resolved — Ticket auto-closed!');
      }
      // Refresh
      const updated = await getSupportTickets();
      const found = (updated.data || []).find(t => t.id === selectedTicket.id);
      if (found) setSelectedTicket(found);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update fail'); }
    finally { setLpgUpdating(null); }
  };

  const handleEscalate = async () => {
    if (!selectedTicket) return;
    setActionLoading(true);
    try {
      const res = await escalateSupportTicket(selectedTicket.id);
      toast.success(res.data.message || 'Ticket escalated');
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Escalate fail');
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      open: { bg: 'bg-blue-100 text-blue-700', icon: <Clock size={12} weight="fill" /> },
      in_progress: { bg: 'bg-amber-100 text-amber-700', icon: <Spinner size={12} weight="fill" /> },
      resolved: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} weight="fill" /> },
      rejected: { bg: 'bg-red-100 text-red-700', icon: <XCircle size={12} weight="fill" /> },
    };
    const s = map[status] || { bg: 'bg-gray-100 text-gray-600', icon: null };
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${s.bg}`}>{s.icon}{status.replace('_', ' ').toUpperCase()}</span>;
  };

  const typeBadge = (type) => {
    const colors = {
      'Booking Mismatch': 'bg-orange-100 text-orange-700',
      'Amount Mismatch': 'bg-red-100 text-red-700',
      'Payment Issue': 'bg-purple-100 text-purple-700',
      'Report Issue': 'bg-cyan-100 text-cyan-700',
      'Bill Payment Issue': 'bg-pink-100 text-pink-700',
      'General Support': 'bg-gray-100 text-gray-700',
    };
    return <span className={`text-xs px-2 py-0.5 rounded font-medium ${colors[type] || 'bg-gray-100 text-gray-600'}`}>{type}</span>;
  };

  const showQtyFields = form.query_type === 'Booking Mismatch';
  const showAmountField = form.query_type === 'Amount Mismatch' || form.query_type === 'Bill Payment Issue';
  const availableQueryTypes = getQueryTypesForRole(user?.role);
  // Role-specific: non-admin roles typically don't need to pick an agency per ticket
  const requireAgency = !['card_vendor', 'gift_card_vendor', 'recharge_vendor', 'electricity_vendor', 'sales_person'].includes(user?.role);

  return (
    <div className="space-y-5" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Headset size={28} className="text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Support / Help Desk</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 self-start sm:self-auto" data-testid="create-ticket-btn">
              <Plus size={18} className="mr-2" /> New Query
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Raise Support Query</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4 mt-2">
              {/* Query Type */}
              <div>
                <Label className="text-sm font-medium">Query Type <span className="text-red-500">*</span></Label>
                <Select value={form.query_type} onValueChange={v => setForm(p => ({ ...p, query_type: v }))}>
                  <SelectTrigger data-testid="ticket-query-type"><SelectValue placeholder="Select Type" /></SelectTrigger>
                  <SelectContent>
                    {availableQueryTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Agency — hidden for vendor/sales roles (not booking-related) */}
              {requireAgency && (
                <div>
                  <Label className="text-sm font-medium">Agency <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={agencies.map(a => ({ value: a.id, label: a.name }))}
                    value={form.agency_id}
                    onValueChange={v => setForm(p => ({ ...p, agency_id: v }))}
                    placeholder="Select Agency"
                    data-testid="ticket-agency"
                  />
                </div>
              )}

              {/* Date + TXN ID — only for booking-related roles */}
              {requireAgency && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Booking Date <span className="text-red-500">*</span></Label>
                    <Input value={form.booking_date} onChange={e => setForm(p => ({ ...p, booking_date: e.target.value }))} placeholder="DD-MM-YYYY" data-testid="ticket-booking-date" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Transaction ID</Label>
                    <Input value={form.transaction_id} onChange={e => setForm(p => ({ ...p, transaction_id: e.target.value.toUpperCase() }))} placeholder="TXN..." className={txnInvalid ? 'border-red-500' : txnValid.checked && txnValid.valid ? 'border-green-500' : ''} data-testid="ticket-txn-id" />
                    {txnValid.checked && txnValid.valid && (
                      <p className="text-[10px] text-green-600 mt-0.5">Valid TXN — {txnValid.agency_name} {txnValid.qty ? `(${txnValid.qty} cyl)` : ''}</p>
                    )}
                    {txnInvalid && (
                      <p className="text-[10px] text-red-600 font-semibold mt-0.5">{txnValid.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Quantity Fields - Booking Mismatch */}
              {showQtyFields && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Booked Qty</Label>
                    <Input type="number" min="0" max={maxQtyInfo.max_qty || undefined} value={form.booked_qty} onChange={e => setForm(p => ({ ...p, booked_qty: e.target.value }))} data-testid="ticket-booked-qty" />
                    {maxQtyInfo.max_qty > 0 && (
                      <p className="text-[10px] text-gray-500 mt-0.5">Max: {maxQtyInfo.max_qty} cylinders</p>
                    )}
                    {bookedQtyExceeded && (
                      <p className="text-[10px] text-red-600 font-semibold mt-0.5">Is date mein sirf {maxQtyInfo.max_qty} booking hua hai!</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Confirmed Qty</Label>
                    <Input type="number" min="0" value={form.confirmed_qty} onChange={e => setForm(p => ({ ...p, confirmed_qty: e.target.value }))} data-testid="ticket-confirmed-qty" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Difference</Label>
                    <div className={`h-9 flex items-center px-3 rounded-md border text-sm font-bold ${difference && difference !== 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`} data-testid="ticket-difference">
                      {difference !== null ? difference : '-'}
                    </div>
                    {difference !== null && difference !== 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Tag size={12} className={confirmedExceedsBooked ? "text-red-500" : "text-orange-500"} />
                        <span className={`text-[10px] font-medium ${confirmedExceedsBooked ? "text-red-600" : "text-orange-600"}`}>
                          {confirmedExceedsBooked ? "Incorrect — Confirmed > Booked" : "Mismatch Detected"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Amount Field - Amount/Bill Payment */}
              {showAmountField && (
                <div>
                  <Label className="text-sm font-medium">Amount</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="Enter amount" data-testid="ticket-amount" />
                </div>
              )}

              {/* Description */}
              <div>
                <Label className="text-sm font-medium">Description <span className="text-red-500">*</span></Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the issue in detail..." rows={3} data-testid="ticket-description" />
              </div>

              {/* File Upload */}
              <div>
                <Label className="text-sm font-medium">Attachments (Optional)</Label>
                <div className="mt-1">
                  {form.file_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {form.file_urls.map((url, i) => (
                        <div key={`tag-${i}`} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs">
                          <span className="truncate max-w-[120px]">{url.split('/').pop()}</span>
                          <button type="button" className="text-red-400 hover:text-red-600" onClick={() => setForm(p => ({ ...p, file_urls: p.file_urls.filter((_, idx) => idx !== i) }))}>x</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input type="file" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.csv" multiple onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="ticket-file-upload" />
                    <div className="flex items-center gap-2 border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-gray-300 cursor-pointer">
                      <UploadSimple size={20} className="text-gray-400" />
                      <span className="text-sm text-gray-500">Upload Excel / Screenshot / PDF</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Already Adjusted Warning */}
              {alreadyAdjusted && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm font-medium" data-testid="already-adjusted-warning">
                  Is agency + date ka balance already adjust ho chuka hai (Ticket: {maxQtyInfo.adjusted_ticket_id}). Duplicate entry nahi hogi.
                </div>
              )}

              {/* Booked Qty Exceeded Warning */}
              {bookedQtyExceeded && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm font-medium">
                  Booked Qty ({form.booked_qty}) is date mein actual bookings ({maxQtyInfo.max_qty}) se zyada hai!
                </div>
              )}

              {/* Confirmed > Booked Warning */}
              {confirmedExceedsBooked && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm font-medium">
                  Incorrect details — Confirmed Qty ({form.confirmed_qty}) booked qty ({form.booked_qty}) se zyada nahi ho sakti. Please correct karein.
                </div>
              )}

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11" disabled={submitting || bookedQtyExceeded || alreadyAdjusted || txnInvalid || confirmedExceedsBooked} data-testid="ticket-submit-btn">
                {(() => {
                  if (submitting) return 'Submitting...';
                  if (alreadyAdjusted) return 'Already Adjusted — Duplicate Not Allowed';
                  if (txnInvalid) return 'Invalid TXN — Check Transaction Number';
                  if (bookedQtyExceeded) return 'Qty Exceeds Actual Bookings';
                  if (confirmedExceedsBooked) return 'Incorrect Details — Please Correct';
                  return 'Submit Query';
                })()}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total', count: stats.total, color: 'text-gray-900', border: 'border-gray-200' },
          { label: 'Open', count: stats.open, color: 'text-blue-600', border: 'border-blue-200' },
          { label: 'In Progress', count: stats.in_progress, color: 'text-amber-600', border: 'border-amber-200' },
          { label: 'Resolved', count: stats.resolved, color: 'text-green-600', border: 'border-green-200' },
          { label: 'Rejected', count: stats.rejected, color: 'text-red-600', border: 'border-red-200' },
        ].map(s => (
          <div key={s.label} className={`bg-white border ${s.border} p-3 rounded-lg`} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ').toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {availableQueryTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {requireAgency && (
          <SearchableSelect
            options={agencyFilterOptions}
            value={filterAgency}
            onValueChange={setFilterAgency}
            placeholder="Agency"
            className="w-44"
            data-testid="filter-agency"
          />
        )}
        <div className="flex items-center gap-1.5">
          <CalendarBlank size={16} className="text-gray-400" />
          <Input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="h-9 w-36 text-sm" />
          <span className="text-gray-400 text-xs">to</span>
          <Input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="h-9 w-36 text-sm" />
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Mobile View */}
            <div className="sm:hidden divide-y divide-gray-100">
              {tickets.length === 0 ? (
                <p className="text-center py-8 text-gray-400 text-sm">No tickets found</p>
              ) : tickets.map(t => (
                <div key={t.id} className="p-3 cursor-pointer hover:bg-gray-50" onClick={() => openDetail(t)} data-testid={`ticket-row-${t.ticket_id}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-xs font-mono text-gray-500">{t.ticket_id}</p>
                      <p className="text-sm font-semibold text-gray-900">{t.query_type}</p>
                    </div>
                    {statusBadge(t.status)}
                  </div>
                  <p className="text-xs text-gray-500">{t.agency_name} | {t.booking_date}</p>
                  {t.tags?.includes('Mismatch Detected') && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-orange-600 font-medium"><Warning size={10} />Mismatch</span>
                  )}
                  <p className="text-xs text-gray-400 mt-1">By: {t.created_by_name}</p>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Ticket ID</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Agency</TableHead>
                    <TableHead className="text-xs">Booker</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Created By</TableHead>
                    <TableHead className="text-xs">Assigned To</TableHead>
                    <TableHead className="text-xs">Tags</TableHead>
                    <TableHead className="text-xs">Level</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">No tickets found</TableCell></TableRow>
                  ) : tickets.map(t => (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-gray-50" data-testid={`ticket-row-${t.ticket_id}`}>
                      <TableCell className="text-xs font-mono text-blue-600">{t.ticket_id}</TableCell>
                      <TableCell>{typeBadge(t.query_type)}</TableCell>
                      <TableCell className="text-sm">{t.agency_name || '-'}</TableCell>
                      <TableCell className="text-sm font-medium text-indigo-700">{t.booker_name || '-'}</TableCell>
                      <TableCell className="text-xs">{t.booking_date}</TableCell>
                      <TableCell className="text-sm">{t.created_by_name}</TableCell>
                      <TableCell className="text-sm">{t.assigned_to_name || <span className="text-gray-400">-</span>}</TableCell>
                      <TableCell>
                        {t.tags?.map(tag => (
                          <span key={tag} className="inline-flex items-center gap-1 text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded font-medium"><Warning size={10} />{tag}</span>
                        ))}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const lvl = t.escalation_level || 1;
                          const cls = lvl === 1 ? 'bg-blue-100 text-blue-700' : lvl === 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                          return <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${cls}`}>L{lvl}</span>;
                        })()}
                      </TableCell>
                      <TableCell>{statusBadge(t.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-blue-600 h-7 text-xs" onClick={() => openDetail(t)} data-testid={`view-ticket-${t.ticket_id}`}>
                          View <ArrowRight size={12} className="ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-blue-600">{selectedTicket.ticket_id}</span>
                  {statusBadge(selectedTicket.status)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Ticket Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Type:</span> {typeBadge(selectedTicket.query_type)}</div>
                  <div><span className="text-gray-500">Agency:</span> <strong>{selectedTicket.agency_name || '-'}</strong></div>
                  <div><span className="text-gray-500">Booking Date:</span> {selectedTicket.booking_date}</div>
                  <div><span className="text-gray-500">TXN ID:</span> <span className="font-mono">{selectedTicket.transaction_id || '-'}</span></div>
                  <div><span className="text-gray-500">Created By:</span> {selectedTicket.created_by_name} ({selectedTicket.created_by_role})</div>
                  <div><span className="text-gray-500">Booker/Staff:</span> <span className="font-semibold text-indigo-700">{selectedTicket.booker_name || '-'}</span></div>
                  <div><span className="text-gray-500">Assigned To:</span> {selectedTicket.assigned_to_name || <span className="text-amber-500">Unassigned</span>}</div>
                </div>

                {/* Escalation Level */}
                {(() => {
                  const level = selectedTicket.escalation_level || 1;
                  const info = ESCALATION_INFO[level];
                  return info ? (
                    <div className={`border rounded-lg p-3 ${info.bg}`} data-testid="escalation-level-display">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Current Escalation</p>
                          <p className={`text-sm font-bold ${info.color}`}>{info.label}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-500">Resolution Time</p>
                          <p className="text-sm font-semibold text-amber-700">{info.time}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Handler: {info.name} ({info.email})</p>
                    </div>
                  ) : null;
                })()}

                {/* Quantity Mismatch */}
                {(selectedTicket.booked_qty != null || selectedTicket.confirmed_qty != null) && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 grid grid-cols-3 gap-3 text-sm">
                    <div><p className="text-gray-500 text-xs">Booked</p><p className="font-bold text-lg">{selectedTicket.booked_qty ?? '-'}</p></div>
                    <div><p className="text-gray-500 text-xs">Confirmed</p><p className="font-bold text-lg">{selectedTicket.confirmed_qty ?? '-'}</p></div>
                    <div>
                      <p className="text-gray-500 text-xs">Difference</p>
                      <p className={`font-bold text-lg ${selectedTicket.difference ? 'text-red-600' : 'text-green-600'}`}>{selectedTicket.difference ?? 0}</p>
                    </div>
                    {selectedTicket.tags?.includes('Mismatch Detected') && (
                      <div className="col-span-3"><span className="inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded font-medium"><Warning size={12} />Mismatch Detected</span></div>
                    )}
                  </div>
                )}

                {/* Amount */}
                {selectedTicket.amount != null && (
                  <div className="bg-gray-50 border rounded-lg p-3 text-sm">
                    <span className="text-gray-500">Amount:</span> <strong className="text-lg">₹{selectedTicket.amount?.toLocaleString('en-IN')}</strong>
                  </div>
                )}

                {/* Description */}
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Card Complaint Verification Panel (Bank PDF match) */}
                <CardComplaintPanel
                  ticket={selectedTicket}
                  isAdmin={isAdmin}
                  onTimelineRefresh={async () => {
                    try {
                      const updated = await getSupportTickets({ ticket_id: selectedTicket.id });
                      const found = (updated.data || []).find(t => t.id === selectedTicket.id);
                      if (found) setSelectedTicket(found);
                    } catch { /* ignore */ }
                  }}
                />

                {/* Attachments */}
                {selectedTicket.file_urls?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Attachments</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.file_urls.map((url, i) => (
                        <a key={`attachment-${i}`} href={`${window.location.origin}${url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs bg-blue-50 px-2 py-1 rounded">
                          {url.split('/').pop()}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolution Info */}
                {selectedTicket.status === 'resolved' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Resolved by {selectedTicket.resolved_by_name}</p>
                    <p className="text-sm">{selectedTicket.resolution_notes}</p>
                    {selectedTicket.adjustment_qty && <p className="text-sm mt-1 font-medium">Qty Adjustment: {selectedTicket.adjustment_qty > 0 ? '+' : ''}{selectedTicket.adjustment_qty} cylinders</p>}
                    {selectedTicket.adjustment_amount && <p className="text-sm mt-1 font-medium">Amount Adjustment: ₹{selectedTicket.adjustment_amount?.toLocaleString('en-IN')}</p>}
                    {selectedTicket.adjustment_txn_id && <p className="text-xs text-gray-500 mt-1">TXN: {selectedTicket.adjustment_txn_id}</p>}
                    {/* Line Items Detail */}
                    {selectedTicket.line_items?.length > 0 && (
                      <div className="mt-2 border-t border-green-200 pt-2">
                        <p className="text-xs font-bold text-gray-700 mb-1">Line-Item Details:</p>
                        <div className="space-y-0.5">
                          {selectedTicket.line_items.map((li, idx) => (
                            <div key={li.lpg_id || `li-${idx}`} className={`flex items-center gap-2 text-xs px-2 py-0.5 rounded ${li.status?.toLowerCase() === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                              <span className="font-mono flex-1">{li.lpg_id}</span>
                              <span className="text-gray-500">{li.qty || 1} cyl</span>
                              <span className={`font-bold ${li.status?.toLowerCase() === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                                {li.status?.toUpperCase()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── LPG Entries Section (hide for Card Vendor tickets — Card Refund Verification handles those) ─── */}
                {selectedTicket.created_by_role !== 'card_vendor' &&
                 !/\d{16}/.test(selectedTicket.description || '') &&
                 (selectedTicket.lpg_entries?.length > 0 || (isAdmin && selectedTicket.status !== 'rejected')) && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-700 px-3 py-2 flex items-center justify-between">
                      <p className="text-white text-xs font-semibold">LPG ID Tracking ({selectedTicket.lpg_entries?.length || 0} entries)</p>
                      {selectedTicket.lpg_entries?.length > 0 && (
                        <div className="flex gap-2 text-[10px]">
                          <span className="bg-green-500 text-white px-1.5 py-0.5 rounded">{selectedTicket.lpg_entries?.filter(e => e.status === 'success').length} OK</span>
                          <span className="bg-red-500 text-white px-1.5 py-0.5 rounded">{selectedTicket.lpg_entries?.filter(e => e.status === 'failed').length} FAIL</span>
                          <span className="bg-amber-500 text-white px-1.5 py-0.5 rounded">{selectedTicket.lpg_entries?.filter(e => e.status === 'pending').length} PENDING</span>
                        </div>
                      )}
                    </div>

                    {/* Add LPG IDs - paste box + excel */}
                    {isAdmin && selectedTicket.status !== 'resolved' && selectedTicket.status !== 'rejected' && (
                      <div className="p-2 bg-gray-50 border-b space-y-2">
                        <div className="flex gap-2">
                          <Textarea
                            value={lpgPasteText}
                            onChange={e => setLpgPasteText(e.target.value)}
                            placeholder="LPG IDs paste karein (ek line mein ek ID)..."
                            rows={2}
                            className="flex-1 text-xs"
                            data-testid="lpg-paste-input"
                          />
                          <div className="flex flex-col gap-1">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-2" onClick={handleAddLpgBulk} data-testid="lpg-paste-add-btn">
                              + Add
                            </Button>
                            <label className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-xs h-7 px-2 rounded flex items-center justify-center" data-testid="lpg-excel-upload">
                              Excel
                              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleLpgExcelUpload} />
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* LPG Entries Table */}
                    {selectedTicket.lpg_entries?.length > 0 && (
                      <div className="max-h-[300px] overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50 text-[10px]">
                              <TableHead className="py-1 px-2">LPG ID</TableHead>
                              <TableHead className="py-1 px-2">Status</TableHead>
                              <TableHead className="py-1 px-2">Resolved By</TableHead>
                              <TableHead className="py-1 px-2">Remarks</TableHead>
                              {isAdmin && selectedTicket.status !== 'resolved' && <TableHead className="py-1 px-2 w-[180px]">Action</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedTicket.lpg_entries.map((entry, idx) => (
                              <TableRow key={entry.lpg_id || `lpg-${idx}`} className={entry.status === 'success' ? 'bg-green-50/50' : entry.status === 'failed' ? 'bg-red-50/50' : ''} data-testid={`lpg-entry-${idx}`}>
                                <TableCell className="text-xs font-mono py-1 px-2">{entry.lpg_id}</TableCell>
                                <TableCell className="py-1 px-2">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                    entry.status === 'success' ? 'bg-green-100 text-green-700' :
                                    entry.status === 'failed' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>{entry.status?.toUpperCase()}</span>
                                </TableCell>
                                <TableCell className="text-[10px] py-1 px-2 text-gray-500">{entry.resolved_by_name || '-'}</TableCell>
                                <TableCell className="text-[10px] py-1 px-2 text-gray-500 max-w-[100px] truncate">{entry.remarks || '-'}</TableCell>
                                {isAdmin && selectedTicket.status !== 'resolved' && (
                                  <TableCell className="py-1 px-2">
                                    {entry.status === 'pending' ? (
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => handleLpgStatusUpdate(entry.lpg_id, 'success', '')}
                                          disabled={lpgUpdating === entry.lpg_id}
                                          className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded hover:bg-green-700 disabled:opacity-50"
                                          data-testid={`lpg-success-${idx}`}
                                        >OK</button>
                                        <button
                                          onClick={() => handleLpgStatusUpdate(entry.lpg_id, 'failed', '')}
                                          disabled={lpgUpdating === entry.lpg_id}
                                          className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700 disabled:opacity-50"
                                          data-testid={`lpg-fail-${idx}`}
                                        >FAIL</button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => handleLpgStatusUpdate(entry.lpg_id, 'pending', '')}
                                        disabled={lpgUpdating === entry.lpg_id}
                                        className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded hover:bg-amber-600 disabled:opacity-50"
                                      >Reset</button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {/* Timeline */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><Clock size={16} /> Timeline</p>
                  <div className="space-y-2 border-l-2 border-gray-200 pl-4">
                    {selectedTicket.timeline?.map((entry, i) => (
                      <div key={`update-${i}`} className="relative">
                        <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white"></div>
                        <p className="text-sm font-medium">{entry.action}</p>
                        <p className="text-xs text-gray-500">{entry.by_name} - {formatDateTime(entry.timestamp)}</p>
                        {entry.notes && <p className="text-xs text-gray-600 mt-0.5">{entry.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin Actions */}
                {isAdmin && selectedTicket.status !== 'resolved' && selectedTicket.status !== 'rejected' && (
                  <div className="border-t pt-4 space-y-3">
                    <p className="text-sm font-medium text-gray-700">Admin Actions</p>

                    {/* Assign */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">Assign To</Label>
                        <SearchableSelect
                          options={staffList.map(s => ({ value: s.id, label: `${s.name} (${s.role})` }))}
                          value={assignTo}
                          onValueChange={setAssignTo}
                          placeholder="Select person"
                          data-testid="ticket-assign-to"
                        />
                      </div>
                      <Button size="sm" onClick={handleAssign} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="ticket-assign-btn">
                        <User size={14} className="mr-1" /> Assign
                      </Button>
                    </div>

                    {/* Add Remark */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">Add Remark</Label>
                        <Input value={remarkText} onChange={e => setRemarkText(e.target.value)} placeholder="Add a note..." data-testid="ticket-remark-input" />
                      </div>
                      <Button size="sm" variant="outline" onClick={handleRemark} disabled={actionLoading} className="h-9" data-testid="ticket-remark-btn">
                        <ChatText size={14} className="mr-1" /> Add
                      </Button>
                    </div>

                    {/* Status Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      {selectedTicket.status === 'open' && (
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8 text-xs" onClick={() => handleStatusChange('in_progress')} disabled={actionLoading} data-testid="ticket-status-in-progress">
                          Mark In Progress
                        </Button>
                      )}
                      {(selectedTicket.escalation_level || 1) < 3 && (
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 h-8 text-xs" onClick={handleEscalate} disabled={actionLoading} data-testid="ticket-escalate-btn">
                          Escalate to Level {(selectedTicket.escalation_level || 1) + 1}
                        </Button>
                      )}
                      <Button size="sm" className="bg-red-500 hover:bg-red-600 h-8 text-xs" onClick={() => handleStatusChange('rejected')} disabled={actionLoading} data-testid="ticket-status-reject">
                        Reject
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs" onClick={() => setResolveOpen(!resolveOpen)} data-testid="ticket-resolve-toggle">
                        <Scales size={14} className="mr-1" /> Resolve with Adjustment
                      </Button>
                    </div>

                    {/* Resolve Form */}
                    {resolveOpen && (
                      <div className="border rounded-lg p-3 bg-green-50 space-y-3">
                        <div>
                          <Label className="text-xs font-medium">Resolution Notes <span className="text-red-500">*</span></Label>
                          <Textarea value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} placeholder="How was this resolved..." rows={2} data-testid="ticket-resolve-notes" />
                        </div>

                        {/* Line-Item Adjustment Section */}
                        <div className="border rounded bg-white p-2 space-y-2">
                          <Label className="text-xs font-bold text-gray-700">Line-Item Adjustment (LPG IDs)</Label>
                          <div className="flex gap-2">
                            <Input
                              value={newLpgId} onChange={e => setNewLpgId(e.target.value)}
                              placeholder="Enter LPG ID..."
                              className="flex-1 text-xs h-8"
                              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addLineItem())}
                              data-testid="line-item-lpg-input"
                            />
                            <Button type="button" size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs px-3" onClick={addLineItem} data-testid="line-item-add-btn">
                              + Add
                            </Button>
                          </div>

                          {lineItems.length > 0 && (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-50">
                                    <TableHead className="text-[10px] py-1 px-2">LPG ID</TableHead>
                                    <TableHead className="text-[10px] py-1 px-2">Qty</TableHead>
                                    <TableHead className="text-[10px] py-1 px-2">Rate</TableHead>
                                    <TableHead className="text-[10px] py-1 px-2">Status</TableHead>
                                    <TableHead className="text-[10px] py-1 px-2 w-8"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {lineItems.map((li, idx) => (
                                    <TableRow key={li.lpg_id || `item-${idx}`} className={li.status.toLowerCase() !== 'success' ? 'bg-red-50' : ''}>
                                      <TableCell className="text-xs font-mono py-1 px-2" data-testid={`line-item-id-${idx}`}>{li.lpg_id}</TableCell>
                                      <TableCell className="py-1 px-2">
                                        <Input type="number" min="1" value={li.qty} onChange={e => updateLineItem(idx, 'qty', parseInt(e.target.value) || 1)} className="h-7 w-14 text-xs" data-testid={`line-item-qty-${idx}`} />
                                      </TableCell>
                                      <TableCell className="text-xs py-1 px-2">₹{li.rate || agencyRate}</TableCell>
                                      <TableCell className="py-1 px-2">
                                        <select
                                          value={li.status}
                                          onChange={e => updateLineItem(idx, 'status', e.target.value)}
                                          className={`text-xs h-7 rounded border px-1 font-medium ${li.status.toLowerCase() === 'success' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}
                                          data-testid={`line-item-status-${idx}`}
                                        >
                                          <option value="success">SUCCESS</option>
                                          <option value="failed">FAILED</option>
                                          <option value="failed/refunded">FAILED/REFUNDED</option>
                                        </select>
                                      </TableCell>
                                      <TableCell className="py-1 px-2">
                                        <button onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600 text-xs" data-testid={`line-item-remove-${idx}`}>✕</button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>

                              {/* Summary */}
                              <div className="mt-2 p-2 rounded bg-gray-50 border text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span>Total Entries:</span>
                                  <span className="font-bold">{lineItems.length} ({lineItems.length - failedItems.length} SUCCESS, {failedItems.length} FAILED)</span>
                                </div>
                                {failedItems.length > 0 && (
                                  <>
                                    <div className="flex justify-between text-red-700">
                                      <span>Refund Qty:</span>
                                      <span className="font-bold">{totalRefundQty} cylinders</span>
                                    </div>
                                    <div className="flex justify-between text-red-700">
                                      <span>Total Refund (Dist + Staff):</span>
                                      <span className="font-bold">₹{totalRefundAmount.toLocaleString('en-IN')}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Fallback: Simple Qty/Amount Adjustment (if no line items) */}
                        {lineItems.length === 0 && (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Qty Adjustment (e.g. -2)</Label>
                                <Input type="number" value={adjQty} onChange={e => setAdjQty(e.target.value)} placeholder="0" data-testid="ticket-adj-qty" />
                              </div>
                              <div>
                                <Label className="text-xs">Amount Adjustment (e.g. +500)</Label>
                                <Input type="number" step="0.01" value={adjAmount} onChange={e => setAdjAmount(e.target.value)} placeholder="0" data-testid="ticket-adj-amount" />
                              </div>
                            </div>
                            <p className="text-[10px] text-gray-500">Ya upar line items add karein ya simple qty/amount use karein.</p>
                          </>
                        )}

                        <Button size="sm" className="bg-green-600 hover:bg-green-700 w-full" onClick={handleResolve} disabled={actionLoading} data-testid="ticket-resolve-btn">
                          {actionLoading ? 'Processing...' : lineItems.length > 0 ? `Resolve — Refund ${failedItems.length} FAILED Items (₹${totalRefundAmount.toLocaleString('en-IN')})` : 'Resolve Ticket'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Support Contact - Only show support email to external users */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="grievance-redressal">
        <div className="bg-slate-800 px-5 py-3">
          <h3 className="text-white text-sm font-semibold tracking-wide uppercase">Customer Support</h3>
        </div>
        <div className="flex items-center gap-4 px-5 py-4">
          <span className="bg-blue-500 text-white text-[10px] font-bold px-2.5 py-1 rounded">Support</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Customer Support</p>
            <p className="text-xs text-gray-500">support@axialup.in</p>
          </div>
          <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded">4-5 Working Days</span>
        </div>
        <div className="px-5 py-2.5 bg-gray-50 text-[10px] text-gray-400">
          For any queries or complaints, please raise a ticket above or write to us at support@axialup.in
        </div>
      </div>
    </div>
  );
}
